import React, {useEffect, useState, useContext, forwardRef, useImperativeHandle} from 'react';
import {Card} from 'antd';
import CircularLoader from '@common/CircularLoader';
import MathJaxHTMLContent from '@common/MathJaxHTMLContent';
import {CheckRounded, InfoRounded, PushPinRounded} from '@mui/icons-material';
import axios from '@common/axios';
import {BrandContext} from '@context/BrandContext';
import lessonResourceService from '../lessonResourceService';

/**
 * LessonPlanEditor - Full refactor using real backend data
 * Fetches saved resources and available resources, allows toggling selections locally.
 * Exposes a save() method to parent for bulk saving all changes.
 */
const LessonPlanEditor = forwardRef(({ lessonPlanId, conceptId, scrollTo }, ref) => {
    const { brandConfig } = useContext(BrandContext);
    const [savedResources, setSavedResources] = useState([]);
    const [availableResources, setAvailableResources] = useState({
        tutorials: [],
        examples: [],
        exercises: [],
        quizzes: [],
        walkthroughs: [],
        videos: [],
        worksheets: [],
        extras: [],
    });
    const [loading, setLoading] = useState(true);

    // Fetch saved resources and available resources
    useEffect(() => {
        if (!lessonPlanId || !conceptId) return;

        setLoading(true);

        Promise.all([
            // Fetch saved resources
            axios.get(`/web/ui/lessonPlanner/lessonPlans/${lessonPlanId}/resources`),
            // Fetch available resources
            lessonResourceService.getTutorials(conceptId, 'LESSON', null, 0, 100),
            lessonResourceService.getTutorials(conceptId, 'GENERAL_CONTENT', ['EXAMPLE'], 0, 100),
            lessonResourceService.getQuestionClusters(conceptId, ['EXERCISE'], null, 0, 100),
            lessonResourceService.getQuestionClusters(conceptId, ['QUICK_QUIZ'], null, 0, 100),
            lessonResourceService.getWalkthroughQuestions(conceptId, 0, 100),
            lessonResourceService.getMediaResources(conceptId, 'VIDEO', 0, 100),
            lessonResourceService.getMediaResources(conceptId, 'WORKSHEET', 0, 100),
            lessonResourceService.getMediaResources(conceptId, 'DESMOS', 0, 50),
            lessonResourceService.getMediaResources(conceptId, 'WIDGET', 0, 50),
            lessonResourceService.getMediaResources(conceptId, 'SCORCHER', 0, 50),
        ])
            .then(([
                savedRes,
                tutorialsPage,
                examplesPage,
                exercisesPage,
                quizzesPage,
                walkthroughsPage,
                videosPage,
                worksheetsPage,
                desmosPage,
                widgetsPage,
                scorcherPage,
            ]) => {
                setSavedResources(savedRes.data);

                setAvailableResources({
                    tutorials: tutorialsPage.content.map(t => ({
                        resourceType: 'TUTORIAL',
                        resourceId: t.tutorialId,
                        title: t.tutorialName,
                        code: t.tutorialNumber ? `Tutorial ${t.tutorialNumber}` : '',
                    })),
                    examples: examplesPage.content.map(t => ({
                        resourceType: 'TUTORIAL',
                        resourceId: t.tutorialId,
                        title: t.tutorialName,
                        code: t.tutorialNumber ? `Example ${t.tutorialNumber}` : '',
                    })),
                    exercises: exercisesPage.content.map(c => ({
                        resourceType: 'QUESTION_CLUSTER',
                        resourceId: c.questionClusterId,
                        title: c.name,
                        questions: c.questionCount,
                    })),
                    quizzes: quizzesPage.content.map(c => ({
                        resourceType: 'QUESTION_CLUSTER',
                        resourceId: c.questionClusterId,
                        title: c.name,
                        questions: c.questionCount,
                    })),
                    walkthroughs: walkthroughsPage.content.map(q => ({
                        resourceType: 'QUESTION',
                        resourceId: q.questionId,
                        title: q.instructions || 'Walkthrough',
                        description: q.contentSummary || '',
                    })),
                    videos: videosPage.content.map(m => ({
                        resourceType: 'MEDIA',
                        resourceId: m.mediaId,
                        title: m.mediaName,
                        description: m.contentSummary || m.mediaDescription || '',
                    })),
                    worksheets: worksheetsPage.content.map(m => ({
                        resourceType: 'MEDIA',
                        resourceId: m.mediaId,
                        title: m.mediaName,
                        description: m.contentSummary || m.mediaDescription || '',
                    })),
                    extras: [
                        ...desmosPage.content.map(m => ({
                            resourceType: 'MEDIA',
                            resourceId: m.mediaId,
                            title: m.mediaName,
                            type: 'desmos',
                        })),
                        ...widgetsPage.content.map(m => ({
                            resourceType: 'MEDIA',
                            resourceId: m.mediaId,
                            title: m.mediaName,
                            type: 'widget',
                        })),
                        ...scorcherPage.content.map(m => ({
                            resourceType: 'MEDIA',
                            resourceId: m.mediaId,
                            title: m.mediaName,
                            type: 'scorcher',
                        })),
                    ],
                });

                setLoading(false);
            })
            .catch(error => {
                console.error('Error loading resources:', error);
                setLoading(false);
            });
    }, [lessonPlanId, conceptId]);

    // Check if a resource is saved (in local state)
    const isResourceSaved = (resourceType, resourceId) => {
        return savedResources.some(r => r.resourceType === resourceType && r.resourceId === resourceId);
    };

    // Toggle resource selection (locally only, no API call)
    const toggleResource = (resource, sectionKey) => {
        const isSaved = isResourceSaved(resource.resourceType, resource.resourceId);

        if (isSaved) {
            // Remove from local state
            setSavedResources(prev => prev.filter(r =>
                !(r.resourceType === resource.resourceType && r.resourceId === resource.resourceId)
            ));
        } else {
            // Add to local state
            const newResource = {
                resourceType: resource.resourceType,
                resourceId: resource.resourceId,
                sectionKey: sectionKey,
                selected: true,
                topPick: false,
            };
            setSavedResources(prev => [...prev, newResource]);
        }
    };

    // Expose save method to parent via ref
    useImperativeHandle(ref, () => ({
        save: () => {
            return new Promise((resolve, reject) => {
                // Build payload from current local state
                const resourcesToSave = savedResources.map(r => ({
                    resourceType: r.resourceType,
                    resourceId: r.resourceId,
                    sectionKey: r.sectionKey,
                    selected: true,
                    topPick: false,
                }));

                // Call bulk update endpoint
                axios.put(`/web/ui/lessonPlanner/lessonPlans/${lessonPlanId}/resources/bulk`, resourcesToSave)
                    .then(() => {
                        resolve();
                    })
                    .catch(error => {
                        reject(error);
                    });
            });
        }
    }));

    // Render a resource row with checkbox
    const renderResourceRow = (resource, sectionKey) => {
        const saved = isResourceSaved(resource.resourceType, resource.resourceId);
        const key = `${resource.resourceType}-${resource.resourceId}`;

        return (
            <div key={key} className="ch-lesson-plan-editor-resource-row">
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '8px' }}>
                    <input
                        type="checkbox"
                        checked={saved}
                        onChange={() => toggleResource(resource, sectionKey)}
                    />
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {resource.code && (
                            <span style={{ color: '#999' }}>{resource.code}</span>
                        )}
                        <MathJaxHTMLContent
                            ElementTag="span"
                            html={resource.title}
                            showLoader={false}
                            style={{ flex: 1 }}
                        />
                        {resource.questions && (
                            <span style={{ color: '#999', whiteSpace: 'nowrap' }}>— {resource.questions} questions</span>
                        )}
                    </div>
                </label>
            </div>
        );
    };

    // Calculate total selected
    const totalSelected = savedResources.length;

    // Scroll to section if requested
    useEffect(() => {
        if (!scrollTo) return;
        const id = `lpe-${scrollTo}`;
        setTimeout(() => {
            document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
    }, [scrollTo]);

    if (loading) {
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <CircularLoader />
                <p>Loading resources...</p>
            </div>
        );
    }

    return (
        <div className="ch-lesson-planner-review">
            <Card className="ch-lesson-planner-review-summary">
                <div className="ch-lesson-planner-review-summary-inner">
                    <div className="ch-lesson-planner-review-summary-icon" aria-hidden="true">
                        <CheckRounded fontSize="small" />
                    </div>
                    <div>
                        <div className="ch-lesson-planner-review-summary-count">
                            {totalSelected} resources selected for your lesson plan
                        </div>
                        <div className="ch-lesson-planner-review-summary-sub">
                            Check or uncheck resources below to customize your lesson.
                        </div>
                    </div>
                </div>
            </Card>

            {/* Always Included Sections */}
            <div id="lpe-summary" className="ch-lesson-plan-editor-section">
                <div style={{ padding: '16px', background: '#f5f5f5', borderRadius: '4px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <PushPinRounded style={{ fontSize: '18px', color: '#666' }} />
                        <strong>Lesson Summary</strong>
                        <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#999' }}>Always included</span>
                    </div>
                    <p style={{ margin: 0, color: '#666' }}>
                        We'll generate a summary of key takeaways based on your selected content.
                    </p>
                </div>
            </div>

            <div id="lpe-overview" className="ch-lesson-plan-editor-section">
                <div style={{ padding: '16px', background: '#f5f5f5', borderRadius: '4px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <InfoRounded style={{ fontSize: '18px', color: '#666' }} />
                        <strong>Lesson Overview</strong>
                        <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#999' }}>Always included</span>
                    </div>
                    <p style={{ margin: 0, color: '#666' }}>Lesson overview and metadata are always included.</p>
                </div>
            </div>

            {/* Tutorials */}
            {availableResources.tutorials.length > 0 && (
                <div id="lpe-tutorials" className="ch-lesson-plan-editor-section">
                    <h3>Textbook Content</h3>
                    <div style={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                        {availableResources.tutorials.map(r => renderResourceRow(r, 'textbookContent'))}
                    </div>
                </div>
            )}

            {/* Examples */}
            {availableResources.examples.length > 0 && (
                <div id="lpe-examples" className="ch-lesson-plan-editor-section">
                    <h3>Examples</h3>
                    <div style={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                        {availableResources.examples.map(r => renderResourceRow(r, 'examples'))}
                    </div>
                </div>
            )}

            {/* Exercises */}
            {availableResources.exercises.length > 0 && (
                <div id="lpe-quiz" className="ch-lesson-plan-editor-section">
                    <h3>{brandConfig?.exercisesName || 'Exercises'}</h3>
                    <div style={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                        {availableResources.exercises.map(r => renderResourceRow(r, 'quiz'))}
                    </div>
                </div>
            )}

            {/* Quizzes */}
            {availableResources.quizzes.length > 0 && (
                <div id="lpe-quizQuestions" className="ch-lesson-plan-editor-section">
                    <h3>Quizzes</h3>
                    <div style={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                        {availableResources.quizzes.map(r => renderResourceRow(r, 'quizQuestions'))}
                    </div>
                </div>
            )}

            {/* Walkthroughs */}
            {availableResources.walkthroughs.length > 0 && (
                <div id="lpe-walkthroughs" className="ch-lesson-plan-editor-section">
                    <h3>{brandConfig?.walkthroughsName || 'Walkthroughs'}</h3>
                    <div style={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                        {availableResources.walkthroughs.map(r => renderResourceRow(r, 'walkthroughs'))}
                    </div>
                </div>
            )}

            {/* Videos */}
            {availableResources.videos.length > 0 && (
                <div id="lpe-videos" className="ch-lesson-plan-editor-section">
                    <h3>{brandConfig?.videosName || 'Videos'}</h3>
                    <div style={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                        {availableResources.videos.map(r => renderResourceRow(r, 'videos'))}
                    </div>
                </div>
            )}

            {/* Worksheets */}
            {availableResources.worksheets.length > 0 && (
                <div id="lpe-worksheets" className="ch-lesson-plan-editor-section">
                    <h3>{brandConfig?.worksheetsName || 'Worksheets'}</h3>
                    <div style={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                        {availableResources.worksheets.map(r => renderResourceRow(r, 'worksheets'))}
                    </div>
                </div>
            )}

            {/* Extras */}
            {availableResources.extras.length > 0 && (
                <div id="lpe-extras" className="ch-lesson-plan-editor-section">
                    <h3>
                        {[
                            brandConfig?.scorcherName || 'Scorcher',
                            brandConfig?.desmosName || 'Desmos',
                            brandConfig?.widgetsName || 'Widgets'
                        ].filter(Boolean).join(', ').replace(/, ([^,]*)$/, ' and $1')}
                    </h3>
                    <div style={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                        {availableResources.extras.map(r => renderResourceRow(r, 'extras'))}
                    </div>
                </div>
            )}
        </div>
    );
});

export default LessonPlanEditor;
